import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChargeCategoryFragment } from 'src/api/api.generated'
import { useCallback, useEffect } from 'react'
import { getCompetenceDate } from '../../../bill/components/bills-list/pos-passerby-bills-list.hook'
import { useHotelDate } from 'src/app/hooks/context/context.hook'

export function useAddNewProductHook({
  currentSelectedChargeCategory,
  checkin,
  checkout,
  onSubmit,
}: {
  currentSelectedChargeCategory: ChargeCategoryFragment | undefined
  onSubmit: (data: ProductSchema) => Promise<void>
  checkin: Date | null
  checkout: Date | null
}) {
  const hotelDate = useHotelDate()
  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProductSchema>({ resolver: zodResolver(schema) })

  useEffect(() => {
    setValue('chargeCategoryId', currentSelectedChargeCategory?.id ?? '')

    if (currentSelectedChargeCategory) {
      setValue('chargeCategory.defaultVat.value', currentSelectedChargeCategory.defaultVat.value)
      setValue('chargeCategory.name', currentSelectedChargeCategory.name)
      setValue('chargeCategory.defaultVatId', currentSelectedChargeCategory.defaultVatId)
      setValue('chargeCategory.defaultVat.code', currentSelectedChargeCategory.defaultVat.code)
    }
  }, [currentSelectedChargeCategory])

  useEffect(() => {
    setValue('competenceDate', getCompetenceDate(hotelDate, checkin, checkout))
  }, [checkin, checkout, hotelDate])

  const formValue = watch()

  const onFormSubmit = useCallback(handleSubmit(onSubmit), [onSubmit, handleSubmit])

  return {
    control,
    onFormSubmit,
    errors,
    setValue,
    formValue,
  }
}

export const schema = z.object({
  chargeCategoryId: z.string().min(1).default(''),
  chargeCategory: z.object({
    name: z.string(),
    defaultVatId: z.string(),
    defaultVat: z.object({
      value: z.number(),
      code: z.string(),
    }),
  }),
  description: z.string().min(1),
  amount: z.number(),
  isActive: z.boolean().default(true),
  competenceDate: z.date(),
})

export type ProductSchema = z.TypeOf<typeof schema>
